import { ChevronUp } from 'lucide-react'
import { type HTMLAttributes, type MouseEvent, useEffect, useId, useState } from 'react'
import { cn } from '../lib/utils'
import { ProgressPip, ProgressPipRest } from './progress-pip'

export interface SectionRailItem {
  id: string
  label: string
  href: string
}

export interface SectionRailGroup {
  id: string
  label: string
  items: SectionRailItem[]
}

export interface SectionRailProps {
  /** Newest/first group starts open; the rest start collapsed. */
  groups: SectionRailGroup[]
  /** The item being read. Its group opens itself if collapsed. */
  activeId?: string
  /** How far through the active item's section the reader is, 0–1. */
  progress?: number
  /** Called on a plain click. Modified clicks (new tab etc.) are left to the browser. */
  onSelect?: (id: string, event: MouseEvent<HTMLAnchorElement>) => void
  'aria-label': string
  className?: string
}

// React 18 drops a JSX `inert`; spreading it as an unknown attribute gets it into the DOM.
const INERT = { inert: '' } as unknown as HTMLAttributes<HTMLDivElement>

function groupOf(groups: SectionRailGroup[], itemId: string | undefined) {
  return itemId ? groups.find((g) => g.items.some((i) => i.id === itemId))?.id : undefined
}

/**
 * A grouped jump-nav for a long page: collapsible group headings, one link per section, and a
 * progress track on the section being read. It doesn't scroll or spy — the page owns both.
 */
export function SectionRail({ groups, activeId, progress = 0, onSelect, 'aria-label': ariaLabel, className }: SectionRailProps) {
  const baseId = useId()
  const activeGroup = groupOf(groups, activeId)
  const [open, setOpen] = useState<Set<string>>(() => new Set([groups[0]?.id, activeGroup].filter((id): id is string => !!id)))

  useEffect(() => {
    if (activeGroup) setOpen((prev) => (prev.has(activeGroup) ? prev : new Set(prev).add(activeGroup)))
  }, [activeGroup])

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })

  return (
    <nav aria-label={ariaLabel} className={className}>
      {groups.map((group, index) => {
        const isOpen = open.has(group.id)
        const panelId = `${baseId}-${group.id}`
        return (
          <div key={group.id} className={cn(index > 0 && 'mt-3.5')}>
            <button
              type="button"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggle(group.id)}
              className="flex items-center gap-2 rounded-sm pt-1.5 pb-2 font-medium font-mono text-foreground text-xs tracking-[0.14em] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:focus-visible:ring-pelorous-300">
              {group.label}
              <ChevronUp
                aria-hidden="true"
                strokeWidth={1.5}
                className={cn(
                  'size-2.5 text-faint transition-transform duration-[250ms] motion-reduce:transition-none',
                  !isOpen && 'rotate-180'
                )}
              />
            </button>
            <div
              id={panelId}
              {...(isOpen ? {} : INERT)}
              className={cn(
                'grid transition-[grid-template-rows] duration-300 ease-settle motion-reduce:transition-none',
                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              )}>
              <ul className="-my-0.5 overflow-hidden py-0.5">
                {group.items.map((item) => {
                  const isActive = item.id === activeId
                  return (
                    <li key={item.id}>
                      <a
                        href={item.href}
                        aria-current={isActive ? 'location' : undefined}
                        onClick={(event) => {
                          if (!onSelect || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
                          onSelect(item.id, event)
                        }}
                        className={cn(
                          'flex items-center gap-3.5 rounded-sm py-[5px] text-sm transition-colors',
                          isActive ? 'font-semibold text-foreground' : 'text-muted-foreground hover:text-foreground'
                        )}>
                        <ProgressPip
                          active={isActive && isOpen}
                          progress={progress}
                          length={34}
                          rest={ProgressPipRest.Track}
                          className={isActive ? '-my-[7px]' : 'my-0'}
                        />
                        {item.label}
                      </a>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        )
      })}
    </nav>
  )
}
